import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';

const professors = [
  {
    id: 1,
    name: "Dr. Raghavendra Mishra",
    title: "Ex IIT Professor",
    expertise: "Advanced Computational Intelligence & AI Systems Engineering",
    image: "/images/one.webp",
    color: "#14f7c1",
  },
  {
    id: 2,
    name: "Dr. Suresh Vasant",
    title: "Ex IIT Professor",
    expertise: "Structural Engineering & Resilient Infrastructure Design",
    image: "/images/secound.webp",
    color: "#14f7c1",
  },
  {
    id: 3,
    name: "Prof. Ananya Gupta",
    title: "Ex IIT Professor",
    expertise: "Next-Gen Computer Science & Intelligent Systems Research",
    image: "/images/third.webp",
    color: "#14f7c1",
  },
  {
    id: 4,
    name: "Dr. Joseph Varghese",
    title: "Ex IIT Professor",
    expertise: "Computational Engineering & Smart Technology Systems",
    image: "/images/fifth.webp",
    color: "#14f7c1",
  },
  {
    id: 5,
    name: "Dr. Kavitha Reddy",
    title: "Ex IIT Professor",
    expertise: "Sustainable Architecture & Intelligent Built Environment Design",
    image: "/images/fourth.webp",
    color: "#6DC1A4",
  },
  {
    id: 6,
    name: "Dr. Sarfaraz Ahmed",
    title: "Ex IIT Professor",
    expertise: "Advanced Structural Design & Seismic Engineering",
    image: "/images/sixth.webp",
    color: "#14f7c1",
  },
];

export default function ProfessorSlider() {
  const { t} = useTranslation();
  const [active, setActive] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [slideDir, setSlideDir] = useState(null);
  const touchStartX = useRef(0);
  const timerRef = useRef(null);
  const total = professors.length;

  // Fixed goTo function
  const goTo = (idx, dir = "right") => {
    if (sliding || idx === active) return;

    const newIdx = (idx + total) % total;
    setSlideDir(dir);
    setSliding(true);

    // After animation completes, update active index
    setTimeout(() => {
      setActive(newIdx);
      setSlideDir(null);
      setTimeout(() => {
        setSliding(false);
      }, 50);
    }, 280);
  };

  const next = () => goTo(active + 1, "right");
  const prev = () => goTo(active - 1, "left");

  // Auto slide
  useEffect(() => {
    timerRef.current = setInterval(next, 400000);
    return () => clearInterval(timerRef.current);
  }, [active]); // Removed sliding dependency to prevent issues

  // Improved Touch Handlers
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    // Pause auto-slide when user touches
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === 0) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 50) {  // Increased threshold for better swipe detection
      if (diff > 0) {
        next();
      } else {
        prev();
      }
    }

    touchStartX.current = 0;

    // Resume auto-slide after swipe
    timerRef.current = setInterval(next, 4000);
  };

  // Also support mouse drag for better UX (desktop testing)
  const onMouseDown = (e) => {
    touchStartX.current = e.clientX;
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const onMouseUp = (e) => {
    if (touchStartX.current === 0) return;

    const diff = touchStartX.current - e.clientX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) next();
      else prev();
    }

    touchStartX.current = 0;
    timerRef.current = setInterval(next, 4000);
  };

  const prof = professors[active];

  const cardAnim = sliding
    ? {
        opacity: 0,
        transform: slideDir === "right" ? "translateX(-35px)" : "translateX(35px)",
      }
    : { 
        opacity: 1, 
        transform: "translateX(0)" 
      };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=DM+Sans:wght@300;400;500&display=swap');
        
        *, *::before, *::after { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0; 
        }
        
        button { 
          cursor: pointer; 
          border: none; 
          background: none; 
          -webkit-tap-highlight-color: transparent; 
        }

        /* Add smooth touch scrolling */
        .slider-container {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>

      <div style={s.page}>
        <div style={s.grid} />

        {/* Header */}
        <div style={s.header}>
          <h1 style={s.title}>
             {t("People Behind the")} {" "}
            <span style={{ color: "#14f7c1" }}> {t("Engine")}</span>
          </h1>
          <p style={s.subtitle}>
             {t("Advised & Supported by")} {" "}
            <strong style={{ color: "#06d4ca", fontWeight: 500 }}>
              {t("Ex IIT Professors")}
            </strong>
          </p>
          <div style={s.divider}>
            <div style={s.dLine} />
            <div style={s.dDot} />
            <div style={{ ...s.dLine, transform: "scaleX(-1)" }} />
          </div>
        </div>

        {/* Card with improved touch events */}
        <div
          style={s.cardOuter}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}   // Important for mouse drag
        >
          <div
            style={{
              ...s.card,
              transition: sliding 
                ? "opacity 0.28s ease, transform 0.28s ease" 
                : "opacity 0.4s ease, transform 0.4s ease",
              ...cardAnim,
            }}
          >
            {/* Ambient glow */}
            <div
              style={{
                ...s.glow,
                background: `radial-gradient(circle, ${prof.color}1a, transparent 70%)`,
              }}
            />

            {/* Corner brackets */}
            <div style={{ ...s.cTL, borderColor: prof.color + "55" }} />
            <div style={{ ...s.cBR, borderColor: prof.color + "55" }} />

            {/* Professor Image */}
            <div style={{ ...s.imgRing, borderColor: prof.color }}>
              <div
                style={{
                  ...s.spinRing,
                  borderTopColor: prof.color,
                  borderRightColor: prof.color + "33",
                  borderBottomColor: prof.color + "33",
                  borderLeftColor: prof.color + "33",
                }}
              />
              <div style={s.imgInner}>
                <img
                  src={prof.image}
                  alt={prof.name}
                  style={s.img}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            </div>

            
            {/* Name */}
            <h2 style={s.profName}>{t(prof.name)}</h2>

            {/* IIT Badge */}
            <div
              style={{
                ...s.iitBadge,
                color: prof.color,
                borderColor: prof.color + "44",
                background: prof.color + "12",
              }}
            >
              ★ {t(prof.title)}
            </div>


            {/* Separator */}
            <div
              style={{
                ...s.sep,
                background: `linear-gradient(90deg, transparent, ${prof.color}cc, transparent)`,
              }}
            />

            {/* Expertise */}
            <p style={s.expertise}>{t(prof.expertise)}</p>
          </div>
        </div>

        {/* Navigation Dots */}
        <div style={s.nav}>
          <div style={s.dots}>
            {professors.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > active ? "right" : "left")}
                style={{
                  ...s.dot,
                  background: i === active ? prof.color : "#ffffff1e",
                  width: i === active ? 28 : 8,
                  height: 8,
                }}
                aria-label={`Go to professor ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* Styles */
const s = {
  page: {
    width: "100%",
    maxWidth: 430,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    paddingBottom: "30px",
  },
  grid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px)",
    backgroundSize: "48px 48px",
    pointerEvents: "none",
  },

  /* Header */
  header: {
    textAlign: "center",
    width: "100%",
    marginBottom: 20,
    position: "relative",
    zIndex: 2,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: "#f0ece0",
    lineHeight: 1.22,
    marginBottom: 10,
    marginTop: 20,
  },
  subtitle: {
    fontSize: 16,
    color: "#999999",
    fontWeight: 300,
    lineHeight: 1.5,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: 18,
  },
  dLine: {
    width: 48,
    height: 1,
    background: "linear-gradient(90deg, transparent, #C9A84C66)",
  },
  dDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#C9A84C",
  },

  /* Card */
  cardOuter: {
    width: "100%",
    position: "relative",
    zIndex: 2,
    userSelect: "none",
    padding: "20px 20px",
    touchAction: "pan-y",   // Important for better swipe
  },
  card: {
    background: "linear-gradient(150deg, #0f0f0f 0%, #1b1b1b 100%)",
    border: "1px solid #ffffff12",
    borderRadius: 24,
    padding: "24px 24px 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: "380px",
  },
  glow: {
    position: "absolute",
    top: -120,
    left: "50%",
    transform: "translateX(-50%)",
    width: 320,
    height: 320,
    borderRadius: "50%",
    pointerEvents: "none",
  },
  cTL: {
    position: "absolute",
    top: 14,
    left: 14,
    width: 20,
    height: 20,
    borderTop: "1.5px solid",
    borderLeft: "1.5px solid",
    borderRadius: "6px 0 0 0",
  },
  cBR: {
    position: "absolute",
    bottom: 14,
    right: 14,
    width: 20,
    height: 20,
    borderBottom: "1.5px solid",
    borderRight: "1.5px solid",
    borderRadius: "0 0 6px 0",
  },

  /* Image */
 
  spinRing: {
    position: "absolute",
    inset: -6,
    borderRadius: "5%",
    borderWidth: 2.5,
    borderStyle: "solid",
    animation: "spin 4s linear infinite",
  },
  imgInner: {
    width: "100%",
    height: "100%",
    borderRadius: "5%",
    overflow: "hidden",
    background: "#111",
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  /* Text */
  iitBadge: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.12em",
    border: "1px solid",
    borderRadius: 30,
    padding: "5px 16px",
    marginBottom: 12,
    position: "relative",
    zIndex: 1,
  },
  profName: {
    fontSize: 21,
    fontWeight: 700,
    color: "#f0ece0",
    lineHeight: 1.3,
    marginBottom: 8,
    position: "relative",
    zIndex: 1,
     marginTop: 12,
  },
  sep: {
    width: "65%",
    height: 1,
    borderRadius: 2,
    marginBottom: 16,
    position: "relative",
    zIndex: 1,
  },
  expertise: {
    fontSize: 15.5,
    color: "#888",
    lineHeight: 1.7,
    fontWeight: 300,
    maxWidth: "92%",
    position: "relative",
    zIndex: 1,
  },

  /* Nav */
  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 12,
    width: "100%",
    position: "relative",
    zIndex: 2,
  },
  dots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
  },
  dot: {
    height: 8,
    borderRadius: 6,
    border: "none",
    padding: 0,
    cursor: "pointer",
    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  },
};